import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import {
  getProjectRules,
  updateProjectRules,
  checkSwitchability,
} from '../../../../infrastructure/api/project-rules'
import {
  ProjectRulesResponse,
  SprintTaskAssociationMode,
} from '../../../../domain/value-object/ProjectRulesVO'
import { receivedProjectRules } from '../../../../store/projectRules'
import { addGlobalMessage, MessageLevel } from '../../../../store/messages'
import { usePageDataIsEdited } from '../../../hooks/usePageDataIsEdited'

type UseProjectRulesFormReturn = {
  rules: ProjectRulesResponse | undefined
  updateSprintAssociationMode: (mode: SprintTaskAssociationMode) => void
  edited: boolean
  isSaving: boolean
  isLoading: boolean
  save: () => Promise<void>
  canSwitchToSingle: boolean
  conflictingCodes: string[]
}

export const useProjectRulesForm = (): UseProjectRulesFormReturn => {
  const dispatch = useDispatch()
  const intl = useIntl()
  const { edited, editedPageData, refreshedPageData } = usePageDataIsEdited()

  const [rules, setRules] = useState<ProjectRulesResponse | undefined>()
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [canSwitchToSingle, setCanSwitchToSingle] = useState(true)
  const [conflictingCodes, setConflictingCodes] = useState<string[]>([])

  const refreshRules = useCallback(async () => {
    const [fetchedRules, switchability] = await Promise.all([
      getProjectRules(),
      checkSwitchability(),
    ])
    setRules(fetchedRules)
    setCanSwitchToSingle(switchability.switchable)
    setConflictingCodes(switchability.conflictingWbsItemCodes ?? [])
    dispatch(receivedProjectRules(fetchedRules))
  }, [dispatch])

  useEffect(() => {
    refreshRules().finally(() => setIsLoading(false))
  }, [refreshRules])

  const updateSprintAssociationMode = useCallback(
    (mode: SprintTaskAssociationMode) => {
      setRules(prev =>
        prev ? { ...prev, sprintTaskAssociationMode: mode } : undefined
      )
      editedPageData()
    },
    [editedPageData]
  )

  const save = useCallback(async () => {
    if (!rules || !edited || isSaving) return
    setIsSaving(true)
    try {
      await updateProjectRules(rules)
      await refreshRules()
      refreshedPageData()
      dispatch(
        addGlobalMessage({
          type: MessageLevel.SUCCESS,
          title: intl.formatMessage({ id: 'projectRules.saveSuccess' }),
        })
      )
    } finally {
      setIsSaving(false)
    }
  }, [rules, edited, isSaving, dispatch, intl, refreshRules, refreshedPageData])

  return {
    rules,
    updateSprintAssociationMode,
    edited,
    isSaving,
    isLoading,
    save,
    canSwitchToSingle,
    conflictingCodes,
  }
}
